import React, { useEffect, useState, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../../components/UI/button";
import { useSystem } from "../../../hooks/use-system";
import callAPI from "../../../api";
import { defaultLanguage } from "../../../constants/languages";
import { Overlay, Tooltip } from "react-bootstrap";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/UI/form";
import { Input } from "../../../components/UI/input";
import {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import { handleApiReturn } from "../../../helpers/logic";
import { mapBackendError } from "../../../helpers/errorHandling";
import InputMask from "react-input-mask";
import { CheckIcon, LoaderVectors } from "../../../constants/icons";

const FormSchema = z.object({
  phonenumber: z
    .string()
    .min(8, { message: "" })
    .refine((value) => isValidPhoneNumber(value), {
      message: "",
    }),
});
export function InputForm({
  popUpText,
  config,
  language,
  setStep,
  handleVerificationRequest,
  handleCarrierCode,
}) {
  const [countrySelected, setCountrySelected] = useState("");
  const [agreed, setAgreed] = useState(
    config.terms_checkbox_switch ? config.default_terms_switch_status : true
  );
  const ref = useRef(null);
  const [shown, showPopover] = useState(false);
  const target = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const system = useSystem();
  const form = useForm({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      phonenumber: countrySelected,
    },
  });

  const isTesting = config.is_testing;

  useEffect(() => {
    if (config.country_code) {
      const country = getCountryCallingCode(config.country_code?.toUpperCase());
      setCountrySelected(country);
      form.setValue("phonenumber", `+${country}`);
    }
  }, [config.country_code, form]);

  var _supportsLocalStorage =
    !!window.localStorage &&
    typeof localStorage.getItem === "function" &&
    typeof localStorage.setItem === "function" &&
    typeof localStorage.removeItem === "function";

  function onSubmit(data) {
    const phone = form.getValues("phonenumber").replace(/\s/g, '');
    const isPhoneValid = isValidPhoneNumber(phone);
    if (!isPhoneValid) {
      setError("Please enter a valid number");
      return;
    }
    if (loading) return;

    setLoading(true);

    if (_supportsLocalStorage) {
      window.localStorage.setItem("phone", phone);
    }

    if (isTesting) {
      setStep((prev) => prev + 1);

      if (config.flow === "CLICK_2_SMS_REDIRECT") {
        setTimeout(() => {
          setStep((prev) => prev + 1);
        }, 3000);
      }
      return;
    }

    callAPI(
      {
        phone_number: phone,
        country_code: config.country_code,
        path: window.location.pathname,
        carrier_sub_code: null,
        session_id: localStorage.getItem("nj_sessionId"),
        token: localStorage.getItem("nj_token"),
        supportStorage: _supportsLocalStorage,
        web_click_id: system.webClickId,
        config_id: system.config.public_id,
        qs: window.location.search,
        language: system.language,
        af_token: config.antifraud_token,
      },
      "initSubscription"
    )
      .then(async (data) => {
        setLoading(false);
        if (data.status !== "success") {
          const supportedLanguage = config?.supported_languages ? Object.keys(config.supported_languages)[0] : null;
          setError(mapBackendError(data.message, 'phone', supportedLanguage));
          return;
        }

        await handleApiReturn(
          config,
          data.payload,
          () => setStep((prev) => prev + 1),
          handleVerificationRequest,
          handleCarrierCode
        );
      })
      .catch((e) => {
        setLoading(false);
      });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full max-w-md flex flex-col justify-center items-center gap-3 mt-3"
      >
        {popUpText && (
          <div
            className={`relative rounded-lg px-3 -mb-6 -mt-5 ${
              !form.formState.isValid && config.animation_enabled
                ? "animate-[wiggle_1s_ease-in-out_infinite]"
                : ""
            }`}
          >
            <p
              className="text-sm text-center font-semibold m-0"
              style={{
                color: config.text_color || "#000",
              }}
              dangerouslySetInnerHTML={{ __html: popUpText }}
            ></p>
          </div>
        )}
        <FormField
          control={form.control}
          name="phonenumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <div
                  className={`relative w-full focus-visible:ring-0 ${
                    form.formState.isValid ? `border-green-500 border-2 ` : ""
                  } ${error ? `border-[#e02f1fe3]` : ""} text-base
                         rounded-lg 
                         ${
                    !form.formState.isValid && config.animation_enabled
                      ? "animate-[wiggle_1s_ease-in-out_infinite]"
                      : ""
                  }`}
                >

                  <InputMask
                    mask={`+${countrySelected.replace(/\d/g, "\\$&")} ${
                      config.phone_mask
                    }`}
                    maskChar="_"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={loading}
                    alwaysShowMask
                    formatChars={{
                      "#": "[0-9]",
                      a: "[A-Za-z]",
                      "*": "[A-Za-z0-9]",
                    }}
                  >
                    {(inputProps) => (
                      <Input
                        iconShow={form.formState.isValid}
                        icon={
                          <div
                            className={` p-1 flex justify-center items-center w-8 h-8 rounded-full`}
                          >
                            <CheckIcon fill={"#04D36B"} />
                          </div>
                        }
                        {...inputProps}
                        onClick={(e) => {
                          if (inputProps.onClick) {
                            inputProps.onClick(e);
                          }
                          const inputEl = e.target;
                          setTimeout(() => {
                            const firstEditableChar = inputEl.value.indexOf('_');
                            if (firstEditableChar !== -1) {
                              inputEl.setSelectionRange(firstEditableChar, firstEditableChar);
                            }
                          }, 0);
                        }}
                        heightClass="h-[45px]"
                        className={`${
                          !form.formState.isValid
                            ? `bg-white`
                            : "bg-transparent"
                        } ${
                          error ? `border-[#e02f1fe3]` : ""
                        } text-base tracking-widest tracking-[3px] focus:outline-none focus-visible:ring-0`}
                      />
                    )}
                  </InputMask>
                </div>
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        {config.terms_checkbox_switch && (
          <div
            className="flex w-full gap-2"
            style={{ color: config.text_color }}
            ref={ref}
          >
            <Overlay
              show={shown}
              target={target}
              placement="bottom"
              container={ref}
              containerPadding={20}
            >
              <Tooltip placement={"bottom"}>Select this checkbox!</Tooltip>
            </Overlay>
            <input
              ref={target}
              id="terms-check"
              onChange={() => setAgreed((prev) => !prev)}
              type={"checkbox"}
              checked={agreed}
            />{" "}
            <label htmlFor="terms-check" className="text-xs">
              {config.translations?.[language || defaultLanguage]?.terms_agreement || ""}
            </label>
            {config.append_terms_link_to_consent && (
              <a
                style={{ color: config.titles_color }}
                href={config.terms_url}
                target="_blank"
                rel="noreferrer"
              >
                {config.terms_text}
              </a>
            )}
          </div>
        )}

        {error && (
          <div className="flex justify-center items-center w-full bg-[#FCEBE9] rounded-lg border-[#E02F1F] border-1">
            <div className={"text-[#e02f1fe3] my-2"}>{error || ""}</div>
          </div>
        )}

        <Button
          className={`flex flex-row gap-2 sm:h-[98px] h-[60px] landscape:h-[55px] px-5 py-4 landscape:py-1.5 landscape:px-4 w-full  ${
            !form.formState.isValid
              ? "cursor-not-allowed "
              : config.animation_enabled
              ? "animate-[wiggle_1s_ease-in-out_infinite]"
              : ""
          } ${
            loading ? "cursor-not-allowed " : "cursor-pointer "
          }  font-semibold text-xl md:text-3xl landscape:text-xl rounded-full tracking-wider`}
          style={{
            backgroundColor: config.button_primary_color || "#e02f1fe3",
            color: config.button_text_color || "#000",
            fontSize: config.button_font_size,
          }}
          type="submit"
          disabled={!form.formState.isValid || loading || !agreed}
        >
          {loading ? (
            <>
              <span className="animate-spin">
                <LoaderVectors />
              </span>
              <div className="flex flex-row justify-end items-center">
                <p
                  className="m-0"
                  style={{ fontSize: config.button_font_size }}
                >
                  {
                    config.texts?.[language || defaultLanguage]?.[0]
                      .button_loading_text
                  }
                </p>
                <span className="animate-bounce">.</span>
                <span className="animate-bounce delay-200">.</span>
                <span className="animate-bounce delay-300">.</span>
              </div>
            </>
          ) : (
            config.texts?.[language || defaultLanguage]?.[0]?.button_text || ""
          )}
        </Button>

        <div className="flex justify-center items-center w-full">
          <span
            className="text-sm text-center"
            style={{
              color: config.text_color || "#000",
            }}
          >
            {config.texts?.[language || defaultLanguage]?.[0]?.button_caption ||
              ""}
          </span>
        </div>
      </form>
    </Form>
  );
}
